import { X } from 'lucide-react';
import type { SessionInfo, AgentState } from '../api/types';
import './MobileAgentSwitcher.css';

// Recency = most recent sign of life (mirrors SessionList's getActivityTs:
// last_active_at = max(human input, autonomous pane motion), with
// fallbacks for older backends).
function getActivityTs(s: SessionInfo): number {
  const a = s.last_active_at;
  if (a && a > 0) return a;
  return s.last_activity && s.last_activity > 0 ? s.last_activity : Number(s.created);
}

function shortAgo(s: SessionInfo): string {
  const diff = Date.now() / 1000 - getActivityTs(s);
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function dotClass(state?: string): string {
  if (state === 'thinking' || state === 'tool') return 'working';
  if (state === 'error' || state === 'needs_login' || state === 'rate_limited_transient') return 'error';
  if (state === 'waiting') return 'waiting';
  return 'available';
}

interface Props {
  sessions: SessionInfo[];
  agentStates: Record<string, AgentState>;
  activeSession: string | null;
  /** Agent names the user has opened, most-recent-first (from App). */
  recents: string[];
  userEmail: string;
  userAliases?: string[];
  userAllowedAgents?: string[];
  onSelect: (name: string) => void;
  onClose: () => void;
}

/**
 * Mobile quick-switcher — a bottom sheet popped by a triple-tap so a driver
 * can jump agents one-thumbed (Alex 2026-06-20). Ordering matters: an admin
 * sees ALL agents, so it leads with what YOU actually opened (recents),
 * then YOUR OWN agents — never strangers' busy bots.
 */
export function MobileAgentSwitcher({
  sessions, agentStates, activeSession, recents,
  userEmail, userAliases, userAllowedAgents, onSelect, onClose,
}: Props) {
  const myEmails = new Set(
    [userEmail, ...(userAliases || [])].filter(Boolean).map((e) => e.toLowerCase()),
  );
  const allowed = new Set(userAllowedAgents || []);
  const isMine = (s: SessionInfo) =>
    myEmails.has((s.owner_email || s.owner || '').toLowerCase()) || allowed.has(s.name);

  const byName = new Map(sessions.filter((s) => !s.pool_name).map((s) => [s.name, s]));

  // 1. Recently opened (any owner — you touched it), in recents order.
  const recentTiles = recents.map((n) => byName.get(n)).filter(Boolean) as SessionInfo[];
  const seen = new Set(recentTiles.map((s) => s.name));
  // 2. Your own agents not already shown, by recent activity.
  const mineTiles = [...byName.values()]
    .filter((s) => isMine(s) && !seen.has(s.name))
    .sort((a, b) => getActivityTs(b) - getActivityTs(a));

  const firstMineIdx = recentTiles.length; // where the "Meine" band starts
  const tiles = [...recentTiles, ...mineTiles].slice(0, 12);

  const renderTile = (s: SessionInfo) => {
    const st = agentStates[s.name]?.state;
    return (
      <button
        key={s.name}
        className={`mas-tile${activeSession === s.name ? ' active' : ''}`}
        onClick={() => { onSelect(s.name); onClose(); }}
      >
        <span className="mas-avatar">
          {s.avatar
            ? <img src={s.avatar} alt="" />
            : <span className="mas-initial">{(s.display_name || s.name).charAt(0).toUpperCase()}</span>}
          <span className={`mas-dot ${dotClass(st)}`} />
        </span>
        <span className="mas-name">{s.display_name || s.name}</span>
        <span className="mas-meta">{shortAgo(s)}</span>
      </button>
    );
  };

  const recentPart = tiles.slice(0, Math.min(firstMineIdx, tiles.length));
  const minePart = tiles.slice(recentPart.length);

  return (
    <div className="mas-overlay">
      <div className="mas-sheet">
        <div className="mas-header">
          <span className="mas-title">Schnellwahl</span>
          <button className="mas-close" onClick={onClose} aria-label="Schließen">
            <X size={18} />
          </button>
        </div>

        <div className="mas-scroll">
          {tiles.length === 0 && (
            <div className="mas-empty">Noch keine Agenten geöffnet.</div>
          )}

          {/* "Meine Agenten" on top, "Zuletzt" at the bottom (Alex 2026-06-20). */}
          {minePart.length > 0 && (
            <>
              <div className="mas-band">Meine Agenten</div>
              <div className="mas-grid">{minePart.map(renderTile)}</div>
            </>
          )}
          {recentPart.length > 0 && (
            <>
              <div className="mas-band">Zuletzt</div>
              <div className="mas-grid">{recentPart.map(renderTile)}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
